import React, { useContext } from "react";
import { useForm } from "react-hook-form";
import {
    createValidationResult,
    FormDialog,
    FormFields,
    FormValidationSummary,
    Stack,
    useNotifications,
    usePerformMultipleActions
} from "sprint-common-web";

import { useOnPremOrdersApi } from "../../../../../common/api/orders-api/useOnPremOrdersApi";
import { OrderRequestStatus } from "../../../../../common/types/OrderRequestStatus";
import { OrderRequestListingContext } from "../../context/OrderRequestListingContext";
import { SendOrderRequestItem, validateDiscardedItems } from "../SendOrderRequestItem";
import { SendOrderRequestSummaryList } from "../SendOrderRequestSummaryList";

interface SendMultipleOrderRequestsToOnPremDialogProps {
    selectedItems: SendOrderRequestItem[];
    onClose(): void;
}

export const SendMultipleOrderRequestsToOnPremDialog: React.VFC<SendMultipleOrderRequestsToOnPremDialogProps> = ({
    onClose
}: SendMultipleOrderRequestsToOnPremDialogProps) => {
    const { createOnPremOrderFromRequest } = useOnPremOrdersApi();

    const { updateOrderRequest, selectedItems } = useContext(OrderRequestListingContext);

    const { success, error: errorNotification } = useNotifications();

    const { formState, handleSubmit } = useForm();
    const { performAllItems, isActionInProgress, items } = usePerformMultipleActions<SendOrderRequestItem>({
        isAvailable: isOrderRequestItemSendable,
        selectedItems
    });

    const formHasErrors = createValidationResult([validateDiscardedItems(items, selectedItems.length)]);
    const areValidOrdersEmpty = !items.length;

    const submit = async () => {
        const results = await sendAllItems();

        if (results.every(({ status }) => status === "fulfilled")) {
            onClose();

            success("Sending to on-prem complete");
        } else errorNotification("Something went wrong while sending order requests");
    };

    const sendAllItems = async () => {
        return performAllItems(async (item) => {
            await createOnPremOrderFromRequest({ orderRequestId: item.id });

            updateOrderRequest({ id: item.id, status: OrderRequestStatus.OnPremProcessing, isModified: true });
        });
    };

    return (
        <Stack>
            <FormDialog
                title={"Send order requests to on-prem"}
                formState={formState}
                text={"Are you sure you want to start sending listed order requests?"}
                isBlocking={isActionInProgress}
                showCloseButton={false}
                submit={{
                    onClick: handleSubmit(submit),
                    disabled: areValidOrdersEmpty || isActionInProgress
                }}
                cancel={{
                    onClick: onClose
                }}
                width={700}
                contentHeight={400}
            >
                <FormFields>
                    <FormValidationSummary validationResult={formHasErrors} />
                </FormFields>
                {!!items.length && <SendOrderRequestSummaryList items={items} />}
            </FormDialog>
        </Stack>
    );
};

const isOrderRequestItemSendable = (item: SendOrderRequestItem) => item.status === OrderRequestStatus.Pending;
